<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="820px"
    @click:outside="cerrarDialogo(false)"
  >
    <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="agregar()">
      <v-card class="rounded-lg elevation-3 overflow-hidden">
        <!-- Toolbar Header Principal estilo Inspección -->
        <v-toolbar height="58" color="secondary" dark flat class="px-3 px-md-4">
          <v-icon left>{{ isNuevo ? 'mdi-tune-variant' : 'mdi-pencil-box-outline' }}</v-icon>
          <span class="text-h6 font-weight-medium">
            {{ isNuevo ? 'Registrar Criterio de Aceptación' : 'Editar Criterio de Aceptación' }}
          </span>

          <v-chip
            v-if="!isNuevo && Item.id"
            color="white"
            text-color="secondary"
            class="font-weight-bold ml-3 px-3 elevation-1"
            style="font-size: 0.95rem; height: 32px"
          >
            <v-icon left size="18" color="secondary">mdi-pound</v-icon>
            ID: {{ Item.id }}
          </v-chip>

          <v-chip
            v-if="Item.costoindicador"
            small
            color="indigo lighten-4"
            text-color="indigo darken-4"
            class="font-weight-bold ml-2 px-3 elevation-1 d-none d-sm-inline-flex"
            style="font-size: 0.85rem; height: 28px"
          >
            <v-icon left size="14" color="indigo darken-3">mdi-chart-line</v-icon>
            {{ Item.costoindicador }}
          </v-chip>

          <v-spacer></v-spacer>

          <v-btn icon dark @click="cerrarDialogo(false)" class="btn-circle-tooltip">
            <v-icon size="20">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4 pa-md-5">
          <!-- SECCIÓN 1: PARÁMETROS DEL INDICADOR -->
          <div class="section-title d-flex align-center mb-3">
            <v-icon color="secondary" small class="mr-2">mdi-tune</v-icon>
            <span class="text-subtitle-2 font-weight-bold text--primary">
              Parámetros del Indicador y Período
            </span>
          </div>

          <!-- Campos cuando es nuevo registro -->
          <v-row dense v-if="isNuevo" class="mb-2">
            <v-col cols="12" sm="6" md="6" class="pa-2">
              <v-autocomplete
                :items="ArrayIndicador"
                label="Indicador *"
                v-model="Item.costoindicador"
                readonly
                dense
                outlined
                prepend-inner-icon="mdi-chart-line"
                class="rounded-lg"
                hide-details="auto"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" sm="6" md="6" class="pa-2">
              <v-autocomplete
                :items="ArrayAños"
                label="Año *"
                v-model="Item.anio"
                dense
                outlined
                prepend-inner-icon="mdi-calendar"
                class="rounded-lg"
                hide-details="auto"
                :rules="[(v) => !!v || 'El año es requerido']"
              ></v-autocomplete>
            </v-col>

            <!-- Modelo - Medida (solo para Costo por km o Costo por hora) -->
            <v-col
              cols="12"
              v-if="
                Item.costoindicador == 'Costo por kilómetro' ||
                Item.costoindicador == 'Costo por hora'
              "
              class="pa-2"
            >
              <v-autocomplete
                :items="ItemsModeloMedida"
                v-model="Item.modelomedida"
                label="Seleccione Modelo - Medida *"
                placeholder="Buscar modelo y medida..."
                :rules="[(v) => !!v || 'Seleccione un Modelo - Medida']"
                dense
                outlined
                prepend-inner-icon="mdi-tire"
                class="rounded-lg"
                hide-details="auto"
              ></v-autocomplete>
            </v-col>

            <!-- Cálculo porcentual (solo para Índice de reencauche) -->
            <v-col
              cols="12"
              sm="6"
              md="6"
              v-if="Item.costoindicador == 'Índice de reencauche'"
              class="pa-2"
            >
              <v-text-field
                label="Criterio objetivo (%) *"
                placeholder="Ej. 85"
                hide-details="auto"
                v-model="Item.criterioobjetivo_calculo"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-percent"
                class="rounded-lg"
                :rules="criterioobjetivocalculo_rule"
                @input="calcularCriterios"
              ></v-text-field>
            </v-col>

            <v-col
              cols="12"
              sm="6"
              md="6"
              v-if="Item.costoindicador == 'Índice de reencauche'"
              class="pa-2"
            >
              <v-text-field
                label="Criterio deficiente (%) *"
                placeholder="Ej. 40"
                hide-details="auto"
                v-model="Item.criteriodeficiente_calculo"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-percent"
                class="rounded-lg"
                :rules="criteriodeficientecalculo_rule"
                @input="calcularCriterios"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Banner informativo cuando es edición -->
          <v-alert
            v-else
            dense
            text
            type="info"
            icon="mdi-information-outline"
            class="rounded-lg mb-3 text-caption"
          >
            Modificando los umbrales de evaluación para el criterio
            <strong>#{{ Item.id }}</strong> ({{ Item.costoindicador || 'Indicador general' }}).
          </v-alert>

          <!-- SECCIÓN 2: VALORES Y RANGOS DE EVALUACIÓN -->
          <div class="section-title d-flex align-center mb-3 mt-3">
            <v-icon color="secondary" small class="mr-2">mdi-target</v-icon>
            <span class="text-subtitle-2 font-weight-bold text--primary">
              Rangos y Límites de Aceptación
            </span>
          </div>

          <v-alert
            dense
            text
            color="secondary"
            icon="mdi-lightbulb-on-outline"
            class="rounded-lg mb-4 text-caption"
          >
            <strong>Esquema de Evaluación:</strong>
            El valor <strong>Objetivo</strong> representa el desempeño óptimo,
            los límites <strong>Tolerables</strong> definen el rango de aceptación,
            y el valor <strong>Deficiente</strong> marca el estado crítico.
          </v-alert>

          <v-row dense>
            <!-- 1. Criterio Objetivo -->
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-text-field
                label="Criterio Objetivo *"
                placeholder="0.00"
                hide-details="auto"
                v-model="Item.criterioobjetivo"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-check-circle-outline"
                class="rounded-lg"
                :rules="criterioobjetivo_rule"
                :hint="infocriterio ? `Condición: ${infocriterio}` : ''"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- 2. Tolerable Máximo -->
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-text-field
                label="Tolerable Máximo *"
                placeholder="0.00"
                hide-details="auto"
                v-model="Item.tolerablemaximo"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-arrow-up-circle-outline"
                class="rounded-lg"
                :rules="[(v) => !!v || 'Criterio tolerable máximo es requerido']"
                hint="Condición: c.o ≤ c.t.max"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- 3. Tolerable Mínimo -->
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-text-field
                label="Tolerable Mínimo *"
                placeholder="0.00"
                hide-details="auto"
                v-model="Item.tolerableminimo"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-arrow-down-circle-outline"
                class="rounded-lg"
                :rules="[(v) => !!v || 'Criterio tolerable mínimo es requerido']"
                hint="Condición: c.d ≤ c.t.min"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- 4. Criterio Deficiente -->
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-text-field
                label="Criterio Deficiente *"
                placeholder="0.00"
                hide-details="auto"
                v-model="Item.criteriodeficiente"
                type="number"
                step="any"
                outlined
                dense
                prepend-inner-icon="mdi-alert-circle-outline"
                class="rounded-lg"
                :rules="criteriodeficiente_rule"
                :hint="infocriteriodeficiente ? `Condición: ${infocriteriodeficiente}` : ''"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Alerta de Validación de Rangos -->
            <v-col cols="12" class="pa-2" v-if="alerta">
              <v-alert
                dense
                type="error"
                text
                icon="mdi-alert-circle"
                class="rounded-lg mb-0"
              >
                {{ mensajeAlerta }}
              </v-alert>
            </v-col>
          </v-row>

          <div class="d-flex align-center mt-3 text-caption text--secondary">
            <v-icon x-small color="error" class="mr-1">mdi-asterisk</v-icon>
            <span>Campos obligatorios</span>
          </div>
        </v-card-text>

        <!-- Botones de Acción / Footer -->
        <v-card-actions class="pa-4 bg-light border-top d-flex justify-end align-center">
          <v-btn
            outlined
            color="grey darken-1"
            class="text-capitalize px-4 rounded-lg mr-2 font-weight-medium"
            @click="cerrarDialogo(false)"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>

          <v-btn
            color="primary"
            elevation="2"
            class="text-capitalize px-5 rounded-lg font-weight-bold"
            :loading="loading"
            :disabled="loading || !valid"
            @click="agregar()"
          >
            <v-icon left small>{{ isNuevo ? 'mdi-content-save' : 'mdi-pencil' }}</v-icon>
            {{ isNuevo ? 'Guardar Criterio' : 'Actualizar Criterio' }}
          </v-btn>
        </v-card-actions>

        <MensajeTransicion
          color="#FA694A"
          :text="mensaje"
          v-if="verAlerta"
          :top="true"
          :bottom="false"
        />
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions, mapState } from "vuex";
import MensajeTransicion from "../Alerta.vue";
import { notif } from "@/plugins/notyf";
import { axios2 as axios } from "@/interceptor/useApi";
import global from "@/global";

export default {
  props: ["dialog", "Item", "Years"],
  components: {
    MensajeTransicion
  },
  created() {
    this.CargarAños();
  },
  data() {
    return {
      ArrayIndicador: [
        "Costo por kilómetro",
        "Costo por hora",
        "Índice de reencauche",
        "Índice de reencauchabilidad"
      ],
      ArrayAños: [],
      alerta: false,
      infocriterio: "",
      infocriteriodeficiente: "",
      habilitarcriterioobjetivo: false,
      habilitarcriteriodeficiente: false,
      mensajeAlerta: "",
      valid: false,
      criterioobjetivo_rule: [],
      criteriodeficiente_rule: [],
      criterioobjetivocalculo_rule: [
        (v) => !!v || "Criterio objetivo es requerido",
        (v) =>
          v <= 100 ||
          "El Índice de reencauche (%): puede ser cero o mayor que uno y máximo 100."
      ],
      criteriodeficientecalculo_rule: [
        (v) => !!v || "Criterio deficiente es requerido",
        (v) =>
          v <= 100 ||
          "El Índice de reencauche (%): puede ser cero o mayor que uno y máximo 100."
      ],
      mensaje: "",
      verAlerta: false,
      loading: false,
      global: global
    };
  },
  computed: {
    ...mapState("navigation", ["Permisos"]),
    ...mapGetters("criterioaceptacion", ["ItemsModeloMedida", "Indicador"]),
    isNuevo() {
      return !this.Item || this.Item.id == null || this.Item.id === "";
    }
  },
  methods: {
    ...mapActions("criterioaceptacion", [
      "agregarCriterioAceptacion",
      "cargarCriterioAceptacion"
    ]),

    async agregar() {
      if (this.$refs.form && !this.$refs.form.validate()) {
        return;
      }

      this.loading = true;

      // Si es nuevo registro, validamos duplicidad con el backend
      if (this.isNuevo) {
        const url = "criteriosaceptacion/consulta";
        let datos = {
          id_cliente: localStorage.getItem("idcliente"),
          indicador: this.Item.costoindicador,
          anio: this.Item.anio,
          modelomedida: this.Item.modelomedida
        };

        try {
          const response = await axios.post(url, datos);
          if (response.data == 1) {
            this.mensajeAlerta = "Este criterio ya fue registrado para el indicador y período seleccionado.";
            this.alerta = true;
            setTimeout(() => {
              this.alerta = false;
            }, 3500);
            this.loading = false;
            return;
          }
        } catch (error) {
          console.error("Error al consultar criterio:", error);
        }
      }

      // Validación lógica de rangos en vista
      if (
        parseFloat(this.Item.criteriodeficiente) < parseFloat(this.Item.criterioobjetivo) &&
        this.Item.costoindicador !== "Índice de reencauche" &&
        this.Item.costoindicador !== "Índice de reencauchabilidad"
      ) {
        this.mensajeAlerta = "El criterio objetivo no debe ser mayor que el criterio deficiente.";
        this.alerta = true;
        setTimeout(() => {
          this.alerta = false;
        }, 3500);
        this.loading = false;
        return;
      }

      if (
        parseFloat(this.Item.tolerablemaximo) <= parseFloat(this.Item.tolerableminimo) &&
        parseFloat(this.Item.tolerablemaximo) >= parseFloat(this.Item.criteriodeficiente) &&
        parseFloat(this.Item.tolerableminimo) >= parseFloat(this.Item.criteriodeficiente)
      ) {
        await this.Registrar();
      } else {
        this.loading = false;
        this.mensajeAlerta = "Revisar los rangos tolerables respecto a los valores de criterio objetivo y deficiente.";
        this.alerta = true;
        setTimeout(() => {
          this.alerta = false;
        }, 4000);
      }
    },

    async Registrar() {
      try {
        const url = "criterioaceptacion/store";
        let datos = {
          id_cliente: localStorage.getItem("idcliente"),
          criterio: this.Item
        };
        await axios.post(url, datos);
        this.cerrarDialogo(true);
      } catch (error) {
        this.loading = false;
        console.error("Error al guardar criterio:", error);
        this.mensajeAlerta = "Ocurrió un error al intentar guardar el criterio.";
        this.alerta = true;
        setTimeout(() => {
          this.alerta = false;
        }, 3500);
      }
    },

    CargarAños() {
      const fecha = new Date();
      let anio_inicio = 2017;
      let anio_final = fecha.getFullYear() + 2;

      for (let i = anio_inicio; i <= anio_final; i++) {
        this.ArrayAños.push(i);
      }
    },

    calcularCriterios() {
      if (this.Item.criterioobjetivo_calculo) {
        this.Item.criterioobjetivo = this.Item.criterioobjetivo_calculo / 100;
      } else {
        if (this.Item.criterioobjetivo !== "" && this.Item.criterioobjetivo) {
          this.Item.criterioobjetivo = "";
        }
      }
      if (this.Item.criteriodeficiente_calculo) {
        this.Item.criteriodeficiente = this.Item.criteriodeficiente_calculo / 100;
      } else {
        if (this.Item.criteriodeficiente !== "" && this.Item.criteriodeficiente) {
          this.Item.criteriodeficiente = "";
        }
      }
    },

    cerrarDialogo(valor = false) {
      if (valor) {
        notif.success(this.isNuevo ? "Criterio registrado con éxito" : "Criterio actualizado con éxito");
        this.cargarCriterioAceptacion();
      }
      this.loading = false;
      this.alerta = false;
      this.$emit("closeDialog");
    },

    cargarFormulario(indicador) {
      if (!indicador) return;
      if (indicador.costoindicador === "Índice de reencauche") {
        this.infocriterio = "0 < c.o ≤ 100";
        this.infocriteriodeficiente = "0 ≤ c.d ≤ 100";
        this.habilitarcriteriodeficiente = true;
        this.habilitarcriterioobjetivo = true;
      } else if (indicador.costoindicador === "Índice de reencauchabilidad") {
        this.infocriterio = "0 < c.o ≤ 10";
        this.infocriteriodeficiente = "0 ≤ c.d ≤ 10";
        this.criterioobjetivo_rule = [
          (v) => !!v || "Criterio objetivo es requerido",
          (v) => v < 11 || "Criterio objetivo debe ser menor o igual a 10"
        ];
        this.criteriodeficiente_rule = [
          (v) => !!v || "Criterio deficiente es requerido",
          (v) => v < 11 || "Criterio deficiente debe ser menor o igual a 10"
        ];
        this.habilitarcriteriodeficiente = false;
        this.habilitarcriterioobjetivo = false;
      } else {
        this.infocriterio = "0 < c.o ≤ 5.0";
        this.infocriteriodeficiente = "c.o < c.d";
        this.criterioobjetivo_rule = [
          (v) => !!v || "Criterio objetivo es requerido",
          (v) => v < 6 || "Criterio objetivo debe ser menor o igual a 5",
          (v) => v > 0 || "Criterio objetivo debe ser mayor a 0"
        ];
        this.criteriodeficiente_rule = [
          (v) => !!v || "Criterio deficiente es requerido"
        ];
        this.habilitarcriteriodeficiente = false;
        this.habilitarcriterioobjetivo = false;
      }
    }
  }
};
</script>

<style scoped>
.section-title {
  border-left: 3px solid #424242;
  padding-left: 8px;
  margin-bottom: 12px;
}

.bg-light {
  background-color: #f8fafc;
}

.border-top {
  border-top: 1px solid #e2e8f0;
}

.btn-circle-tooltip {
  background: rgba(255, 255, 255, 0.16) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  max-width: 36px !important;
  padding: 0 !important;
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-decoration: none !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-circle-tooltip ::v-deep .v-btn__content {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  line-height: 1 !important;
}

.btn-circle-tooltip ::v-deep .v-icon {
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.btn-circle-tooltip:hover {
  background: rgba(255, 255, 255, 0.28) !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
}
</style>
